/* global console, setTimeout, clearTimeout */
import React, { useEffect } from "react";
import { Divider } from "@fluentui/react-divider";
import { makeStyles } from "@griffel/react";
import RoomFilters from "./RoomFilters";
import RoomList from "./RoomList";
import { ADDIN_VERSION } from "../../version";
import { Room } from "../domain/Room";
import { findAvailableRooms } from "../../service/ActionHandler";
import { registerAppointmentListeners, removeAppointmentListeners } from "../../service/OfficeEvents";

const useStyles = makeStyles({
  root: {
    minHeight: "100vh",
  },
  versionStyle: {
    fontSize: "0.8em",
    color: "grey",
  },
});

const App = (): React.JSX.Element => {
  const styles = useStyles();

  const [buildingId, setBuildingId] = React.useState("");
  const [floorId, setFloorId] = React.useState("");
  const [arrangeTypeId, setArrangeTypeId] = React.useState("");
  const [capacity, setCapacity] = React.useState<number | null>(null);
  const [selectedAttributes, setSelectedAttributes] = React.useState<string[]>([]);
  const [rooms, setRooms] = React.useState([] as Room[]);
  const [loading, setLoading] = React.useState(true);
  const [loadingRooms, setLoadingRooms] = React.useState(false);
  const [initialized, setInitialized] = React.useState(false);

  /*
   * Finding rooms via the manual refresh button disables all controls, by the loading toggle.
   * A manual refresh is never aborted / ignored, hence checkProceed is true by default and
   * all controls remain disabled until the action completes.
   * Triggered updates only disable the manual refresh button by passing the loadingRooms toggle.
   * They are aborted / ignored once the checkProceed function returns false. The checkProceed
   * function return false once the parameters used to launch the update become obsolete.
   */
  const updateRoomList = (setLoadingToggle: (value: boolean) => void, checkProceed: () => boolean) => {
    setLoadingToggle(true);
    console.log(`Find rooms for [${buildingId},${floorId},${arrangeTypeId},${capacity},[${selectedAttributes}]]`);
    findAvailableRooms(buildingId, floorId, arrangeTypeId, capacity, selectedAttributes, setRooms, checkProceed)
      .catch((reason: any) => {
        console.log(
          `Find rooms for [${buildingId},${floorId},${arrangeTypeId},${capacity},[${selectedAttributes}]] failed.`
        );
        console.log(reason);
        if (checkProceed()) {
          setRooms([]);
          // TODO show 'offline' warning somewhere, depending on reason?
        }
      })
      .finally(() => {
        if (checkProceed()) {
          setLoadingToggle(false);
        }
      });
  };

  /*
   * Use an effect to automatically refresh the room list when a relevant filter is changed and to
   * register event handlers that refresh the room list when a relevant appointment change is detected.
   * Note the event handlers must be reregistered after every filter change - otherwise they would keep
   * using stale copies of the React state variables that define the filters.
   */
  useEffect(() => {
    // do not run the effect before initialization (of the filters) is completed
    if (!initialized) return;

    let ignore = false;
    let listenersRegistered = false;
    // this function is used to stop the call chain when the current parameters are obsolete
    const checkProceed = () => !ignore;
    const filterDescription = `[${buildingId},${floorId},${arrangeTypeId},${capacity},[${selectedAttributes}]]`;

    // add a delay especially to avoid redundant calls when spinner controls are used
    setTimeout(() => {
      if (!checkProceed()) return;

      // reload the room list for the filter changes
      updateRoomList(setLoadingRooms, checkProceed);

      /*
       * Also reregister the appointment listener with the correct filter.
       * Add a delay after detecting an appointment change, so only one update
       * will be started for a change that triggers multiple events.
       */
      let timer: any;
      listenersRegistered = true;
      registerAppointmentListeners(filterDescription, () => {
        // stop the previous timer and start a new one
        clearTimeout(timer);
        timer = setTimeout(() => {
          if (!checkProceed()) return;

          // also stop the call chain once a new timer is started for a next event
          let currentTimer = timer;
          const checkProceedInTimer = () => checkProceed() && currentTimer == timer;
          updateRoomList(setLoadingRooms, checkProceedInTimer);
        }, 200);
      });
    }, 200);
    // Return a cleanup function to ensure stale responses are ignored.
    // React runs this function before starting the next scheduled effect.
    return () => {
      ignore = true;
      if (listenersRegistered) removeAppointmentListeners(filterDescription);
    };
  }, [buildingId, floorId, arrangeTypeId, capacity, selectedAttributes, initialized]);

  return (
    <div className={styles.root}>
      <Divider inset alignContent="end" className={styles.versionStyle}>
        {`v${ADDIN_VERSION}`}
      </Divider>
      <RoomFilters
        buildingId={buildingId}
        setBuildingId={setBuildingId}
        floorId={floorId}
        setFloorId={setFloorId}
        arrangeTypeId={arrangeTypeId}
        setArrangeTypeId={setArrangeTypeId}
        capacity={capacity}
        setCapacity={setCapacity}
        selectedAttributes={selectedAttributes}
        setSelectedAttributes={setSelectedAttributes}
        loading={loading}
        setLoading={setLoading}
        setRooms={setRooms}
        initialized={initialized}
        setInitialized={setInitialized}
      />
      <RoomList
        buildingId={buildingId}
        floorId={floorId}
        arrangeTypeId={arrangeTypeId}
        capacity={capacity}
        attributes={selectedAttributes}
        rooms={rooms}
        loading={loading || loadingRooms || !initialized}
        initialized={initialized}
        updateRoomList={() => updateRoomList(setLoading, () => true)}
      />
    </div>
  );
};

export default App;
